import { Layout } from "@/components/layout/Layout";
import { Link } from "wouter";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BrokerCard } from "@/components/shared/BrokerCard";
import { AdSpace } from "@/components/shared/AdSpace";
import { SocialLinksCard } from "@/components/shared/SocialLinksCard";
import { CryptoExchangeCard } from "@/components/shared/CryptoExchangeCard";
import { LOGO_IMAGE_WIDTH, sizedUploadUrl } from "@/lib/imageUrl";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Check, Zap, BarChart3, TrendingUp, Users, ArrowRight, Shield, Award, PlayCircle, Bitcoin, Brain, Globe, Calendar, Sparkles, Clock, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
// @ts-ignore - module exists but types might be delayed
import Autoplay from "embla-carousel-autoplay";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";
import {
  PARTNER_TAG_ICONS,
  PARTNER_THEMES,
  getThemeFromLegacy,
  type PartnerSlide,
  type PartnerThemeKey,
  type PartnerTagIconKey,
} from "@/home/partnerSlider";
import { ECOSYSTEM_ICONS, normalizeEcosystemCards, type EcosystemCard } from "@/home/ecosystemCards";
// Hero background plate. Deliberately a public URL rather than a Vite import:
// the hero is the LCP element on a phone and client/index.html preloads it, and
// a preload can only name a file that already exists at build time — an imported
// asset has no filename until after the build. Regenerate with
// `python scripts/optimize-images.py`; keep these paths in sync with the
// <link rel="preload"> in client/index.html.
const HERO_BG_SRCSET = "/img/hero-bg-800.webp 800w, /img/hero-bg-1600.webp 1600w";
const HERO_BG_SIZES = "100vw";
const HERO_BG_FALLBACK = "/img/hero-bg-1600.webp";
const HERO_BG_INTRINSIC = { width: 1600, height: 800 } as const;
// Card fallback for a listing that has no image of its own. Imported rather than
// referenced as "/src/assets/…": that path only resolves under the Vite dev
// server, so in production the fallback 404'd and the card rendered a broken
// image.
import newsPlaceholderAsset from "@/assets/news-placeholder.webp";
import {
  newsDateTimeLabel,
  newsIsoDateTime,
  newsReadingTimeMin,
  newsViewsLabel,
  sortNewsLatestFirst,
  visitorTimeZoneLabel,
} from "@/components/news/types";
// "Active trader" faces. Self-hosted professional portrait assets avoid hotlinks,
// licensing ambiguity, and another network origin in the LCP viewport.
import traderAvatar1 from "@/assets/avatars/malaysia-trader-1.svg";
import traderAvatar2 from "@/assets/avatars/malaysia-trader-2.svg";
import traderAvatar3 from "@/assets/avatars/malaysia-trader-3.svg";
import traderAvatar4 from "@/assets/avatars/malaysia-trader-4.svg";
import { subscribeBrokerCatalogUpdates } from "@/lib/live-updates";

type BonusOffer = {
  id: number;
  brokerId: number | null;
  slug?: string | null;
  title: string;
  description: string | null;
  bonusType: string | null;
  bonusAmount: string | null;
  terms: string | null;
  expiresAt: string | null;
  active: boolean;
  featured?: boolean | null;
  displayOrder?: number | null;
};

type Broker = {
  id: number;
  name: string;
  slug: string;
  logo?: string | null;
  website?: string | null;
  ctaUrl?: string | null;
  minDeposit?: string | null;
  maxLeverage?: string | null;
  spreadsFrom?: string | null;
  spreads?: string | null;
  regulators?: { name: string }[];
  overallRating?: any;
  reviewsCount?: number | null;
  featured?: boolean | null;
  homepageFeatured?: boolean | null;
  homepageOrder?: number | null;
  displayOrder?: number | null;
};

type CryptoExchange = {
  id: number;
  name: string;
  slug: string;
  logo?: string | null;
  tagline?: string | null;
  overallRating?: any;
  spotTakerFee?: string | null;
  spotMakerFee?: string | null;
  totalCoinsListed?: number | null;
  featured?: boolean | null;
};

type NewsArticle = {
  id: number;
  title: string;
  slug: string;
  excerpt?: string | null;
  image?: string | null;
  category?: string | null;
  publishedAt?: string | null;
  createdAt?: string | null;
};

type HomepageConfig = {
  hero?: Record<string, any>;
  featuredBlocks?: Record<string, any>;
  partnerSlider?: Record<string, any>;
  sidebarPromos?: Record<string, any>;
  adLabels?: Record<string, any>;
  translations?: Record<string, any>;
};

/**
 * Resolves the admin-configured hero background.
 *
 * The stored value can legitimately be one of three things: empty (= use the
 * bundled default), a leftover *source-tree* path from when the hero was a
 * `@/assets/…` import that only ever resolved under the Vite dev server, or a
 * real uploaded/custom URL. Only the third is used as-is; the first two fall back
 * to the shipped default, which is what stops a stale `src/assets/hero-bg.jpg`
 * row in the homepage config from rendering a broken hero in production.
 */
function resolveHomepageAssetPath(value: unknown, fallback: string) {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  const normalised = raw.replace(/\\/g, "/").toLowerCase();
  if (normalised.startsWith("src/assets/") || normalised.startsWith("/src/assets/")) {
    return fallback;
  }
  return raw;
}

const ACTIVE_TRADER_AVATARS = [
  traderAvatar1,
  traderAvatar2,
  traderAvatar3,
  traderAvatar4,
];

function SquareLogoBadge({
  logoUrl,
  initials,
  alt,
}: {
  logoUrl?: string | null;
  initials: string;
  alt: string;
}) {
  const [failed, setFailed] = useState(false);
  const canShowLogo = !!logoUrl && !failed;
  return (
    <div className="w-12 h-12 bg-[#070b14] rounded-xl border border-white/5 flex items-center justify-center overflow-hidden group-hover:border-primary/30 transition-colors">
      {canShowLogo ? (
        <img
          src={sizedUploadUrl(String(logoUrl), LOGO_IMAGE_WIDTH)}
          alt={alt}
          className="w-full h-full object-contain p-1.5"
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="font-black text-lg text-primary">{initials}</span>
      )}
    </div>
  );
}

export default function Home() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [edgeEmail, setEdgeEmail] = useState("");
  const [edgeLoading, setEdgeLoading] = useState(false);
  const [edgeDone, setEdgeDone] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);

  const handleCompareToggle = (id: string, selected: boolean) => {
    setSelectedForCompare((prev) => {
      if (selected) {
        if (prev.includes(id) || prev.length >= 3) return prev;
        return [...prev, id];
      }
      return prev.filter((item) => item !== id);
    });
  };

  useEffect(() => {
    document.body.classList.toggle("fx-broker-compare-active", selectedForCompare.length > 0);
    return () => document.body.classList.remove("fx-broker-compare-active");
  }, [selectedForCompare.length]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== "fxhub:homepageConfigUpdatedAt") return;
      qc.invalidateQueries({ queryKey: ["/api/homepage-config"] });
    };
    const onBroadcast = () => {
      qc.invalidateQueries({ queryKey: ["/api/homepage-config"] });
    };

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("fxhub");
      bc.addEventListener("message", (evt: MessageEvent) => {
        const data: any = evt.data;
        if (data?.type === "homepageConfigUpdated") onBroadcast();
      });
    } catch {
      bc = null;
    }

    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      try {
        bc?.close();
      } catch {
        // ignore
      }
    };
  }, [qc]);

  useEffect(() => {
    return subscribeBrokerCatalogUpdates(() => {
      qc.invalidateQueries({ queryKey: ["/api/brokers"] });
    });
  }, [qc]);

  const { data: homepageConfig } = useQuery<HomepageConfig | null>({
    queryKey: ["/api/homepage-config"],
    queryFn: async () => {
      const res = await fetch("/api/homepage-config", { cache: "no-store" });
      if (!res.ok) return null;
      return res.json();
    },
  });

  const { data: bonusOffers = [] } = useQuery<BonusOffer[]>({
    queryKey: ["/api/bonuses"],
    queryFn: async () => {
      const res = await fetch("/api/bonuses");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: brokers = [] } = useQuery<Broker[]>({
    queryKey: ["/api/brokers"],
    queryFn: async () => {
      const res = await fetch("/api/brokers");
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const { data: cryptoExchanges = [] } = useQuery<CryptoExchange[]>({
    queryKey: ["/api/crypto-exchanges"],
    queryFn: async () => {
      const res = await fetch("/api/crypto-exchanges");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: newsArticles = [] } = useQuery<NewsArticle[]>({
    queryKey: ["/api/news"],
    queryFn: async () => {
      const res = await fetch("/api/news");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const isBonusExpired = (b: BonusOffer) => {
    if (!b.active) return true;
    if (b.expiresAt) {
      const ms = new Date(b.expiresAt).getTime();
      if (Number.isFinite(ms) && ms < Date.now()) return true;
    }
    return false;
  };

  const activeBonuses = bonusOffers.filter((b) => !isBonusExpired(b)).slice(0, 4);
  const activeBonusesCount = bonusOffers.filter((b) => !isBonusExpired(b)).length;

  const endsInLabel = (expiresAt: string | null) => {
    if (!expiresAt) return "Limited time";
    const ms = new Date(expiresAt).getTime();
    if (!Number.isFinite(ms)) return "Limited time";
    const diff = ms - Date.now();
    if (diff <= 0) return "Ended";
    const h = Math.floor(diff / (60 * 60 * 1000));
    if (h < 24) return `Ends in ${h}h`;
    const d = Math.floor(h / 24);
    return `Ends in ${d}d`;
  };

  const coerceRecord = (v: any): Record<string, any> => {
    if (!v) return {};
    if (typeof v === "object") return v;
    if (typeof v === "string") {
      try {
        const parsed = JSON.parse(v);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    }
    return {};
  };

  const promoCfg = useMemo(() => {
    const sidebarPromos = coerceRecord((homepageConfig as any)?.sidebarPromos);

    const toObjList = (v: any): any[] => {
      if (!Array.isArray(v)) return [];
      return v.filter((x) => x && typeof x === "object");
    };

    const legacyExclusive = coerceRecord((sidebarPromos as any)?.exclusiveOffer);
    const legacyHot = coerceRecord((sidebarPromos as any)?.hotOffer);
    const legacySponsored = coerceRecord((sidebarPromos as any)?.sponsoredOffer);

    const rawExclusive = toObjList((sidebarPromos as any)?.exclusiveOffers);
    const rawHot = toObjList((sidebarPromos as any)?.hotOffers);
    const rawSponsored = toObjList((sidebarPromos as any)?.sponsoredOffers);

    const toStr = (v: any, fallback: string) =>
      typeof v === "string" && v.trim() ? v.trim() : fallback;
    const toBool = (v: any, fallback: boolean) => (v === undefined ? fallback : !!v);
    const toArr = (v: any, fallback: string[]) => {
      if (Array.isArray(v)) {
        const out = v.map((x) => String(x ?? "").trim()).filter(Boolean);
        return out.length ? out : fallback;
      }
      if (typeof v === "string") {
        const out = v
          .split("\n")
          .map((x) => x.trim())
          .filter(Boolean);
        return out.length ? out : fallback;
      }
      return fallback;
    };

    const normalizeExclusive = (ex: any) => {
      const enabled = toBool(ex?.enabled, true);
      if (!enabled) return null;
      return {
        id: typeof ex?.id === "string" ? ex.id : "",
        badgeText: toStr(ex?.badgeText, "EXCLUSIVE"),
        adLabel: toStr(ex?.adLabel, "AD"),
        logoUrl: typeof ex?.logoUrl === "string" ? ex.logoUrl : "",
        brandName: toStr(ex?.brandName, "BINANCE"),
        brandSubtitle: toStr(ex?.brandSubtitle, "World's Largest Exchange"),
        offerTitle: toStr(ex?.offerTitle, "$100 BONUS"),
        offerSubtitle: toStr(ex?.offerSubtitle, "+ 20% Fee Discount Forever"),
        features: toArr(ex?.features, ["Instant Signup", "600+ Coins", "Low Fees", "Mobile App"]),
        buttonText: toStr(ex?.buttonText, "Claim Your Bonus"),
        buttonHref: toStr(ex?.buttonHref, "/crypto/binance"),
      };
    };

    const normalizeHot = (hot: any) => {
      const enabled = toBool(hot?.enabled, true);
      if (!enabled) return null;
      return {
        id: typeof hot?.id === "string" ? hot.id : "",
        offerTagText: toStr(hot?.offerTagText, "HOT OFFER"),
        sponsoredTagText: toStr(hot?.sponsoredTagText, "SPONSORED"),
        title: toStr(hot?.title, "Trade Crypto with $0 Fees!"),
        subtitle: toStr(
          hot?.subtitle,
          "Join Binance and get 20% discount on trading fees + $100 welcome bonus.",
        ),
        features: toArr(hot?.features, ["Instant Deposits", "600+ Coins"]),
        buttonText: toStr(hot?.buttonText, "Claim Bonus Now"),
        buttonHref: toStr(hot?.buttonHref, "/crypto/binance"),
      };
    };

    const normalizeSponsored = (sp: any) => {
      const enabled = toBool(sp?.enabled, true);
      if (!enabled) return null;
      return {
        id: typeof sp?.id === "string" ? sp.id : "",
        tagText: toStr(sp?.tagText, "SPONSORED OFFER"),
        title: toStr(sp?.title, "Get $10,000 Trading Capital"),
        subtitle: toStr(
          sp?.subtitle,
          "Join the world's most trusted prop firm challenge. 85% profit split, instant payouts.",
        ),
        buttonText: toStr(sp?.buttonText, "Apply Now"),
        buttonHref: toStr(sp?.buttonHref, "/broker-comparison"),
      };
    };

    const exclusiveOffers = (
      rawExclusive.length
        ? rawExclusive
        : Object.keys(legacyExclusive).length
          ? [legacyExclusive]
          : []
    )
      .map(normalizeExclusive)
      .filter(Boolean) as any[];

    const hotOffers = (
      rawHot.length
        ? rawHot
        : Object.keys(legacyHot).length
          ? [legacyHot]
          : []
    )
      .map(normalizeHot)
      .filter(Boolean) as any[];

    const sponsoredOffers = (
      rawSponsored.length
        ? rawSponsored
        : Object.keys(legacySponsored).length
          ? [legacySponsored]
          : []
    )
      .map(normalizeSponsored)
      .filter(Boolean) as any[];

    return { exclusiveOffers, hotOffers, sponsoredOffers };
  }, [homepageConfig]);

  const asArray = <T,>(value: unknown): T[] => {
    if (Array.isArray(value)) return value as T[];
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? (parsed as T[]) : [];
      } catch {
        return [];
      }
    }
    return [];
  };
  const getBrokerName = (brokerId: number | null) => {
    if (!brokerId) return "FX";
    const broker = brokers.find(b => b.id === brokerId);
    return broker ? broker.name.substring(0, 2).toUpperCase() : "FX";
  };

  const getBrokerAvatar = (brokerId: number | null) => {
    const broker = brokerId ? brokers.find((b) => b.id === brokerId) : null;
    const name = broker?.name || "Forex Hub";
    const initials = broker ? broker.name.substring(0, 2).toUpperCase() : "FX";
    const logo = broker?.logo || null;
    const slug = broker?.slug || null;
    return { name, initials, logo };
  };

  const getRegulatorsLabel = (b: Broker) => {
    const regs = asArray<{ name?: string }>((b as any).regulators).map((r) => r?.name).filter(Boolean);
    return regs.length ? regs.slice(0, 3).join(", ") : "—";
  };

  const featuredPartnersFallback = [
    {
      name: "EXNESS",
      tagline: "Instant Withdrawals & Low Spreads",
      bonus: "Claim $50 Bonus",
      logoColor: "from-amber-400 to-yellow-600",
      badgeColor: "bg-black text-white hover:bg-gray-900",
      textColor: "text-black",
      link: "/broker/exness"
    },
    {
      name: "XM GLOBAL",
      tagline: "30% Welcome Bonus + VPS",
      bonus: "Start with $30 Free",
      logoColor: "from-blue-600 to-indigo-900",
      badgeColor: "bg-white text-blue-900 hover:bg-gray-100",
      textColor: "text-white",
      link: "/broker/xm"
    },
    {
      name: "IC MARKETS",
      tagline: "Raw Spreads from 0.0 Pips",
      bonus: "Open Raw Account",
      logoColor: "from-emerald-500 to-teal-900",
      badgeColor: "bg-white text-emerald-900 hover:bg-gray-100",
      textColor: "text-white",
      link: "/broker/ic-markets"
    }
  ];

  const featuredPartnersFallbackSlides: PartnerSlide[] = [
    {
      id: "exness",
      title: "EXNESS",
      subtitle: "Instant Withdrawals & Low Spreads",
      buttonLabel: "Claim $50 Bonus",
      buttonLink: "/broker/exness",
      theme: "amber",
      tags: [
        { label: "Top Tier Regulation", icon: "scale" },
        { label: "Instant Deposits", icon: "bolt" },
      ],
    },
    {
      id: "xm",
      title: "XM GLOBAL",
      subtitle: "30% Welcome Bonus + VPS",
      buttonLabel: "Start with $30 Free",
      buttonLink: "/broker/xm",
      theme: "blue",
      tags: [
        { label: "Verified Broker", icon: "shield" },
        { label: "Fast Onboarding", icon: "bolt" },
      ],
    },
    {
      id: "ic",
      title: "IC MARKETS",
      subtitle: "Raw Spreads from 0.0 Pips",
      buttonLabel: "Open Raw Account",
      buttonLink: "/broker/ic-markets",
      theme: "emerald",
      tags: [
        { label: "Top Tier Regulation", icon: "scale" },
        { label: "Instant Deposits", icon: "bolt" },
      ],
    },
  ];

  const featuredPartners = useMemo(() => {
    const rawItems = (homepageConfig as any)?.partnerSlider?.items;
    if (!Array.isArray(rawItems) || !rawItems.length) return featuredPartnersFallbackSlides;

    return rawItems
      .map((it: any, idx: number) => {
        const themeRaw = String(it?.theme || it?.themeKey || "").trim();
        const theme: PartnerThemeKey =
          (themeRaw && (themeRaw in PARTNER_THEMES) ? (themeRaw as PartnerThemeKey) : null) ||
          getThemeFromLegacy(it?.logoColor) ||
          "emerald";

        const tags = Array.isArray(it?.tags)
          ? it.tags
              .map((t: any) => {
                const label = String(t?.label || "").trim();
                const iconRaw = String(t?.icon || "").trim();
                const icon = (iconRaw && (iconRaw in PARTNER_TAG_ICONS) ? (iconRaw as PartnerTagIconKey) : null);
                return icon && label ? ({ label, icon } as const) : null;
              })
              .filter(Boolean)
              .slice(0, 3)
          : [];

        return {
          id: String(it?.id || it?.slug || it?.title || it?.name || idx),
          title: String(it?.title || it?.name || "").trim(),
          subtitle: String(it?.subtitle || it?.tagline || "").trim(),
          buttonLabel: String(it?.buttonLabel || it?.bonus || "").trim(),
          buttonLink: String(it?.buttonLink || it?.link || "/").trim(),
          theme,
          tags: (tags as any) || [],
        } satisfies PartnerSlide;
      })
      .filter((s: any) => s.title && s.buttonLink);
  }, [homepageConfig]);

  const heroCfg = useMemo(() => {
    const raw = (homepageConfig as any)?.hero;
    return raw && typeof raw === "object" ? raw : {};
  }, [homepageConfig]);

  const heroCopy = useMemo(() => {
    const raw = (heroCfg as any)?.copy;
    return raw && typeof raw === "object" ? raw : {};
  }, [heroCfg]);

  const adPlacements = useMemo(() => {
    const raw = (homepageConfig as any)?.adLabels;
    const placements = raw && typeof raw === "object" ? (raw as any).placements : null;
    return placements && typeof placements === "object" ? placements : {};
  }, [homepageConfig]);

  const getAdPlacement = (
    key: "home_leaderboard" | "home_rectangle" | "home_skyscraper",
    defaults: { label: string; slotKey: string; fallbackSlotKeys: string[] },
  ) => {
    const v = (adPlacements as any)?.[key];
    const label = typeof v?.label === "string" && v.label.trim() ? v.label.trim() : defaults.label;
    const slotKey = typeof v?.slotKey === "string" && v.slotKey.trim() ? v.slotKey.trim() : defaults.slotKey;
    // Always keep the shared pool as a fallback so an ad added to the generic
    // slot still appears here when the dedicated slot has no active creative.
    const fallbackSlotKeys = Array.from(
      new Set([...(defaults.fallbackSlotKeys || []), defaults.slotKey].filter((k) => k && k !== slotKey)),
    );
    return { label, slotKey, fallbackSlotKeys };
  };

  const leaderboardAd = getAdPlacement("home_leaderboard", {
    label: "Premium Global Spotlight",
    slotKey: "home_leaderboard",
    fallbackSlotKeys: ["leaderboard"],
  });
  const rectangleAd = getAdPlacement("home_rectangle", {
    label: "Global Premium Spotlight",
    slotKey: "home_rectangle",
    fallbackSlotKeys: ["rectangle"],
  });
  const skyscraperAd = getAdPlacement("home_skyscraper", {
    label: "Premium Vertical Sponsor",
    slotKey: "home_skyscraper",
    fallbackSlotKeys: ["skyscraper"],
  });

  const heroBadgeText = (heroCfg as any)?.badgeText || "Live Market Portal 2026";
  const heroTitlePrefix = (heroCfg as any)?.titlePrefix || "Trade with";
  const heroTitleHighlight = (heroCfg as any)?.titleHighlight || "Confidence.";
  const heroTitleSuffix = (heroCfg as any)?.titleSuffix || "Scale with Precision.";
  const heroDescription =
    (heroCfg as any)?.description ||
    "The global benchmark for broker & crypto exchange transparency. Access institutional-grade tools, verified robots, and low-latency VPS hosting.";
  const heroBg = resolveHomepageAssetPath((heroCfg as any)?.backgroundImage, HERO_BG_FALLBACK);
  /**
   * Only the shipped default can be served as a responsive set — a custom URL
   * from Admin → Homepage is a single file we know nothing about, so it is used
   * as-is.
   */
  const heroBgIsDefault = heroBg === HERO_BG_FALLBACK;
  const heroPrimaryCta = (heroCfg as any)?.primaryCta || {};
  const heroSecondaryCta = (heroCfg as any)?.secondaryCta || {};
  const primaryCtaLabel = heroPrimaryCta.label || "Compare Brokers";
  const primaryCtaHref = heroPrimaryCta.href || "/brokers";
  const secondaryCtaLabel = heroSecondaryCta.label || "Crypto Exchanges";
  const secondaryCtaHref = heroSecondaryCta.href || "/crypto";

  const ecosystemTitle = heroCopy.ecosystemTitle || "Ecosystem of Success";
  const ecosystemSubtitle =
    heroCopy.ecosystemSubtitle ||
    "The infrastructure you need to compete with institutional desks.";
  const ecosystemCards = useMemo(
    () => normalizeEcosystemCards((heroCfg as any)?.ecosystemCards, { fallback: true }),
    [heroCfg],
  );
  const isEcosystemSlider = ecosystemCards.length > 2;

  const renderEcosystemCard = (card: EcosystemCard) => {
    const Icon = ECOSYSTEM_ICONS[card.icon]?.Icon || ECOSYSTEM_ICONS.server.Icon;
    const href = String(card.buttonLink || "/").trim() || "/";

    return (
      <Link href={href}>
        <Card className="bg-[#161e2e] border-white/5 hover:border-primary/50 transition-all duration-300 group cursor-pointer overflow-hidden relative min-h-[280px] sm:min-h-[330px] sm:aspect-[11/10] sm:max-h-[390px]">
          <div className="absolute right-3 top-3 opacity-5 group-hover:opacity-10 transition-opacity sm:right-0 sm:top-0 sm:p-4">
            <Icon className="h-24 w-24 sm:h-32 sm:w-32" />
          </div>
          <CardContent className="relative z-10 flex h-full flex-col p-6 sm:p-8">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary transition-transform group-hover:scale-110 sm:mb-6 sm:h-14 sm:w-14">
              <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
            </div>
            <h3
              className="mb-3 text-[1.65rem] font-black uppercase leading-[1.02] tracking-tight text-white sm:text-xl"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {card.title}
            </h3>
            <p
              className="text-sm font-medium leading-relaxed text-gray-400 sm:text-[15px]"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {card.description}
            </p>
            <div className="mt-auto pt-6">
              <Button
                variant="outline"
                className="h-11 w-full rounded-xl border-white/10 bg-white/5 px-5 text-[11px] font-black uppercase tracking-[0.24em] text-white transition-all hover:border-transparent hover:bg-primary hover:text-black sm:h-12 sm:w-auto sm:px-8 sm:text-[10px]"
              >
                {card.buttonLabel}
              </Button>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };

  const featuredBrokerIds = useMemo(() => {
    const ids = (homepageConfig as any)?.featuredBlocks?.brokers;
    return Array.isArray(ids) ? ids.map((n: any) => Number(n)).filter((n: any) => Number.isFinite(n)) : [];
  }, [homepageConfig]);

  const featuredBrokers = useMemo(() => {
    if (!brokers.length) return [];
    const byId = new Map(brokers.map((b) => [b.id, b] as const));
    const fromCfg = featuredBrokerIds.map((id) => byId.get(id)).filter(Boolean) as Broker[];

    const ranked = brokers
      .filter((b) => Number(b.displayOrder || 0) >= 1 && Number(b.displayOrder || 0) <= 10)
      .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));
    const homepageSelected = brokers
      .filter((b) => !!b.homepageFeatured)
      .sort((a, b) => Number(a.homepageOrder || 0) - Number(b.homepageOrder || 0));

    const picked = new Map<number, Broker>();
    for (const broker of [...ranked, ...homepageSelected, ...fromCfg, ...brokers]) {
      if (!picked.has(broker.id)) picked.set(broker.id, broker);
      if (picked.size >= 3) break;
    }
    return Array.from(picked.values());
  }, [brokers, featuredBrokerIds]);

  const featuredCryptoIds = useMemo(() => {
    const ids = (homepageConfig as any)?.featuredBlocks?.cryptoExchanges;
    return Array.isArray(ids) ? ids.map((n: any) => Number(n)).filter((n: any) => Number.isFinite(n)) : [];
  }, [homepageConfig]);

  const featuredCrypto = useMemo(() => {
    if (!cryptoExchanges.length) return [];
    const byId = new Map(cryptoExchanges.map((e) => [e.id, e] as const));
    const fromCfg = featuredCryptoIds.map((id) => byId.get(id)).filter(Boolean) as CryptoExchange[];
    if (fromCfg.length) return fromCfg.slice(0, 3);
    return cryptoExchanges.slice(0, 3);
  }, [cryptoExchanges, featuredCryptoIds]);

  const marketInsights = useMemo(() => {
    // Sorted here as well as in the API. `publishedAt` is nullable and MySQL
    // sorts NULLs last in a DESC sort, so an article saved without a publish
    // date used to sit below older ones instead of at the top.
    return sortNewsLatestFirst(newsArticles || []).slice(0, 4);
  }, [newsArticles]);

  /**
   * The visitor's timezone, resolved once per render, purely for the timestamp
   * tooltip — `newsDateTimeLabel` already formats in the device's zone. Showing
   * the zone as well means a reader in Dhaka and a reader in London can each
   * confirm what they are looking at instead of guessing.
   */
  const localTimeZone = visitorTimeZoneLabel();

  const REVEAL_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
  const revealProps = (delay = 0) => ({
    initial: { opacity: 0, y: 18, filter: "blur(6px)" },
    whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
    viewport: { once: true, margin: "-120px" },
    transition: { duration: 0.65, ease: REVEAL_EASE, delay },
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0a101f] pt-10 pb-12 text-white sm:pt-14 sm:pb-16 md:py-24 xl:py-28">
        <div className="absolute inset-0 z-0">
          {/*
            The hero plate is the LCP element on a phone, so it gets the LCP
            treatment:

            - `fetchpriority="high"` puts it ahead of the bundle's other
              requests instead of queueing behind them.
            - The `<picture>`/`srcset` set lets a 412px phone pull the 16.4 KiB
              800w plate instead of the 39.9 KiB 1600w one. The whole set is
              56.3 KiB of WebP where the single JPEG it replaces was 116.7 KiB,
              and the JPEG was being scaled up from 1600x800 to a full-height
              phone hero anyway.
            - `width`/`height` (the image's real intrinsic size) give the
              renderer the aspect ratio up front, which is what clears the
              "image elements do not have explicit width and height" audit and
              keeps the plate from contributing to layout shift.
            - `decoding="async"` keeps the decode off the main thread, which is
              the same thread React is booting on.

            It stays a plain `<img>` (inside a dimming overlay at 30% opacity)
            rather than a CSS background so that it is a discoverable LCP
            candidate at all — and matches the preload in client/index.html.
          */}
          <picture>
            {heroBgIsDefault ? (
              <source type="image/webp" srcSet={HERO_BG_SRCSET} sizes={HERO_BG_SIZES} />
            ) : null}
            <img
              src={heroBg}
              alt="Trading Background"
              width={HERO_BG_INTRINSIC.width}
              height={HERO_BG_INTRINSIC.height}
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover opacity-30 mix-blend-overlay"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a101f]/80 to-[#0a101f]"></div>
        </div>
        
        <div className="container relative z-10 mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-8 md:gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(300px,34rem)] xl:gap-10 2xl:grid-cols-[minmax(0,1fr)_minmax(320px,36rem)]">
            {/*
              `@container` turns this column into the query container for everything
              inside it. The hero copy and the two CTAs are sized from THIS column, not
              from the viewport, because the column is not a constant fraction of the
              viewport: it is 288px on a 320px phone, 544px on a tablet, only 376px on a
              1024–1279px laptop (where the featured-partner carousel takes its 544px
              first), and 608px on a wide desktop. A `vw`-based clamp cannot serve all
              four — at 1024px it rendered the headline 8.6% wider than its own column.
            */}
            <div className="@container min-w-0 max-w-[34rem] space-y-6 text-center sm:space-y-8 lg:max-w-[38rem] lg:text-left animate-in fade-in slide-in-from-left duration-700">
              <div className="inline-flex max-w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 lg:justify-start">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                <span className="text-primary text-xs font-bold uppercase tracking-wider">{heroBadgeText}</span>
              </div>
              
              {/*
                Sized in `cqw` — per cent of the column above — so the headline fills
                the same 93% of its column on every screen and breaks in the same place
                for a given piece of copy. The old `8vw` clamp was measured at 4 lines
                filling only 44–71% of a 608px column on desktop, and at 108% (i.e.
                wider than its own column, clipped by `overflow-x: hidden`) on a 1024px
                laptop. `text-balance` evens the rag when a longer headline does wrap,
                and `break-words` is the last-resort guard so no single word can ever
                push past the column edge.

                Container queries need Chrome 105 / Safari 16.0 / Firefox 110, which is
                older than the baseline Tailwind v4 already requires for this stylesheet
                — so `cqw` cannot be the thing that breaks an otherwise-working page.
              */}
              <h1 className="break-words text-balance text-[clamp(1.75rem,10.5cqw,4.5rem)] font-heading font-extrabold leading-[1.02] tracking-tight">
                {heroTitlePrefix}{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-yellow-300 to-primary animate-pulse">
                  {heroTitleHighlight}
                </span>
                <span className="mt-1 block text-white sm:mt-2">{heroTitleSuffix}</span>
              </h1>
              
              <p className="mx-auto max-w-[36rem] text-base leading-relaxed text-gray-400 sm:text-lg md:text-xl lg:mx-0">
                {heroDescription}
              </p>
              
              {/*
                One row, both widths. The row used to be `flex-col` below `sm`, so on a
                phone the two buttons stacked, and each carried a 16rem (256px) minimum
                width that made it impossible to ever seat them side by side.
                `min-w-max` + `grow` is what makes "side by side, never overlapping"
                structural rather than arithmetic: an item can never be narrower than
                its own label, and because the row is still `flex-wrap`, two labels that
                genuinely cannot share a line drop to two lines instead of colliding.
                The sizes step on container queries, so the pills scale with the column
                (376px on a 1024px laptop, 608px on a wide desktop) instead of with the
                viewport — `md:` sizes are what wrapped this row onto two lines today.
              */}
              <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 lg:justify-start">
                <Link href={primaryCtaHref} className="min-w-max grow">
                  <Button size="lg" className="h-12 w-full rounded-xl bg-primary px-2 text-xs font-bold text-primary-foreground shadow-[0_0_20px_rgba(234,179,8,0.3)] hover:bg-primary/90 @xs:px-2.5 @xs:text-[13px] @sm:px-3 @sm:text-sm @md:h-14 @md:px-5 @md:text-[15px] @lg:px-6 @lg:text-base @xl:h-16 @xl:px-8 @xl:text-lg">
                    {primaryCtaLabel}
                  </Button>
                </Link>
                <Link href={secondaryCtaHref} className="min-w-max grow">
                  <Button size="lg" className="h-12 w-full rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 px-2 text-xs font-bold text-white shadow-[0_0_20px_rgba(249,115,22,0.3)] hover:from-orange-600 hover:to-yellow-600 @xs:px-2.5 @xs:text-[13px] @sm:px-3 @sm:text-sm @md:h-14 @md:px-5 @md:text-[15px] @lg:px-6 @lg:text-base @xl:h-16 @xl:px-8 @xl:text-lg">
                    {/* `gap-2` on the button already spaces the icon; the old `mr-2`
                        doubled it, and the icon is dropped entirely on the narrowest
                        phones where 16px of icon is a tenth of the pill. */}
                    <Bitcoin className="hidden h-5 w-5 @xs:inline" /> {secondaryCtaLabel}
                  </Button>
                </Link>
              </div>

               <div className="flex flex-col items-center gap-4 pt-2 sm:flex-row sm:justify-center sm:gap-8 sm:pt-4 lg:justify-start">
                  <div className="flex justify-center -space-x-3">
                   {ACTIVE_TRADER_AVATARS.map((src, i) => (
                     <div key={src} className="h-10 w-10 overflow-hidden rounded-full border-2 border-[#0a101f] bg-gray-700 shadow-[0_8px_20px_rgba(0,0,0,0.28)]">
                       {/*
                          width/height are the SVG viewport, so the intrinsic ratio
                          is known before decode.
                        */}
                        <img
                          src={src}
                          alt={`Active trader ${i + 1}`}
                          width={96}
                          height={96}
                         loading="lazy"
                         decoding="async"
                         className="h-full w-full object-cover"
                       />
                     </div>
                   ))}
                   </div>
                 <div className="text-sm text-center lg:text-left">
                    <span className="font-bold text-white">50k+</span> <span className="text-gray-500">Global Traders Active</span>
                  </div>
               </div>
            </div>
            
            <div className="relative mx-auto w-full min-w-0 max-w-[34rem] animate-in fade-in zoom-in duration-1000 delay-300 lg:justify-self-end xl:max-w-[36rem]">
              <div className="absolute -inset-3 rounded-[2.5rem] bg-gradient-to-r from-primary via-yellow-400 to-primary blur-2xl opacity-20 animate-pulse"></div>
              {/* Featured Partner Slider */}
              <Carousel 
                className="relative z-10 w-full"
                plugins={[
                  Autoplay({
                    delay: 5000,
                  }),
                ]}
                opts={{
                  loop: true,
                }}
              >
                <CarouselContent>
                  {featuredPartners.map((partner, index) => (
                    <CarouselItem key={index}>
                      {(() => {
                        const theme = PARTNER_THEMES[partner.theme] || PARTNER_THEMES.emerald;
                        const rawTags = Array.isArray(partner.tags) ? partner.tags : [];
                        const tags: { label: string; icon: PartnerTagIconKey }[] = rawTags
                          .map((t: any) => ({
                            label: String(t?.label || "").trim(),
                            icon: String(t?.icon || "").trim() as PartnerTagIconKey,
                          }))
                          .filter((t) => t.label && t.icon && t.icon in PARTNER_TAG_ICONS)
                          .slice(0, 2);

                        const footerTags = tags.length
                          ? tags
                          : [
                              { label: "Top Tier Regulation", icon: "scale" as const },
                              { label: "Instant Deposits", icon: "bolt" as const },
                            ];

                        return (
                           <div
                             className="group relative cursor-pointer overflow-hidden rounded-[2rem] border border-white/10 bg-[#161e2e]/80 p-1 shadow-2xl backdrop-blur-2xl transition-all duration-500 sm:rounded-[2.5rem]"
                             onClick={() => (window.location.href = partner.buttonLink)}
                           >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                         <div className="relative z-10 p-4 sm:p-5 lg:p-6">
                             <div className="mb-4 border-b border-white/8 pb-3 sm:mb-6 sm:pb-4">
                               <div className="grid grid-cols-[2.5rem_1fr] items-center gap-x-3 gap-y-1 text-left sm:grid-cols-[2.75rem_1fr]">
                                 <div className="row-span-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-emerald-400/25 bg-emerald-400/10 transition-transform group-hover:scale-110 sm:h-11 sm:w-11">
                                   <Shield className="h-5.5 w-5.5 text-emerald-400 sm:h-6 sm:w-6" />
                                 </div>
                                 <div className="text-[10px] font-black uppercase leading-none tracking-[0.22em] text-emerald-400 sm:text-[11px]">
                                   Verified
                                 </div>
                                 <div className="text-[9px] font-extrabold uppercase leading-none tracking-[0.28em] text-gray-400 transition-colors group-hover:text-primary sm:text-[10px]">
                                   Premium Global Partner
                                 </div>
                               </div>
                             </div>
                            <div className={`relative flex min-h-[200px] flex-col items-center justify-center overflow-hidden rounded-[1.75rem] border border-white/10 bg-gradient-to-br ${theme.gradientClass} px-4 py-7 text-center shadow-2xl transition-transform duration-500 group-hover:scale-[1.02] sm:min-h-[240px] sm:p-8 lg:min-h-[260px] lg:px-8 lg:py-10`}>
                              {/* Self-hosted tile — see CryptoExchangeCard for why. */}
                              <div className="absolute inset-0 bg-[url('/img/carbon-fibre.png')] opacity-10"></div>
                              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                              <div className={`z-10 max-w-[15rem] break-words text-[clamp(1.6rem,5vw,2.8rem)] font-black uppercase leading-[0.95] tracking-tight drop-shadow-2xl transition-transform group-hover:scale-105 sm:max-w-[18rem] ${theme.textClass}`}>{partner.title}</div>
                              <p className={`z-10 mt-3 max-w-[26rem] px-2 text-[11px] font-black uppercase tracking-[0.18em] opacity-90 drop-shadow-lg sm:mt-4 sm:text-sm ${theme.textClass}`}>{partner.subtitle}</p>
                              <Button className="z-10 mt-5 min-h-12 w-full max-w-[24rem] whitespace-normal rounded-2xl border-none bg-white px-4 py-3 text-sm font-black uppercase tracking-tight text-black shadow-2xl transition-all group-hover:translate-y-[-4px] hover:bg-black hover:text-white sm:mt-6 sm:min-h-14 sm:text-base lg:text-lg">
                                {partner.buttonLabel}
                              </Button>
                            </div>
                            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-3 px-2 text-center sm:mt-7 sm:gap-x-6">
                              {footerTags.slice(0, 2).map((t, i) => {
                                const Icon = PARTNER_TAG_ICONS[t.icon]?.Icon;
                                return (
                                  <div key={`${t.label}-${i}`} className="flex items-center gap-4 sm:gap-6">
                                    <div className="flex items-center text-[9px] font-black uppercase tracking-[0.18em] text-gray-500 transition-colors group-hover:text-white sm:text-[10px]">
                                      {Icon ? <Icon className="w-4 h-4 mr-2 text-primary" /> : <Award className="w-4 h-4 mr-2 text-primary" />}
                                      {t.label}
                                    </div>
                                    {i === 0 ? <div className="hidden sm:block h-4 w-px bg-white/10" /> : null}
                                  </div>
                                );
                              })}
                           </div>
                         </div>
                          </div>
                        );
                      })()}
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        </div>
      </section>

      {/* Intelligence Hub */}
      <section className="py-12 md:py-18 bg-[#0a101f] border-b border-white/5">
        <div className="container mx-auto px-4 max-w-[1500px]">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-4 py-1.5">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-primary">New Features</span>
              </div>
              <h2 className="mt-4 text-3xl md:text-4xl font-heading font-black text-white tracking-tight">
                Intelligence <span className="text-primary">Hub</span>
              </h2>
              <p className="mt-2 text-sm md:text-base text-gray-400 max-w-2xl">
                AI-powered insights and interactive tools to stay ahead of the markets.
              </p>
            </div>
            <Link href="/intelligence-hub">
              <Button variant="outline" className="w-full md:w-auto border-white/10 bg-white/5 rounded-xl text-white">
                Explore Intelligence Hub <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {[
              {
                title: "AI News Digest",
                badge: "AI Powered",
                desc:
                  "Daily AI-generated market summaries with sentiment analysis and key insights from global markets.",
                href: "/tools/news-digest",
                Icon: Brain,
                tone: "from-violet-500/12 via-white/0 to-white/0",
                badgeCls: "bg-violet-500/15 text-violet-300 border-violet-500/30",
                iconCls: "bg-violet-500/20 text-violet-200",
              },
              {
                title: "Global Coverage Map",
                badge: "Interactive",
                desc:
                  "Explore broker and exchange availability across 50+ regions with interactive comparison tools.",
                href: "/tools/global-coverage",
                Icon: Globe,
                tone: "from-emerald-500/12 via-white/0 to-white/0",
                badgeCls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
                iconCls: "bg-emerald-500/20 text-emerald-200",
              },
              {
                title: "Economic Calendar",
                badge: "Live Data",
                desc:
                  "Track 100+ economic events with live countdowns, impact ratings, and local timezone support.",
                href: "/tools/economic-calendar",
                Icon: Calendar,
                tone: "from-primary/12 via-white/0 to-white/0",
                badgeCls: "bg-primary/15 text-primary border-primary/30",
                iconCls: "bg-primary/20 text-primary",
              },
            ].map((c, idx) => (
              <motion.div
                key={c.href}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: idx * 0.06 }}
                whileHover={{ y: -4 }}
                className="h-full"
              >
                <Card className="relative h-full overflow-hidden bg-[#111827]/70 border border-white/5 rounded-2xl">
                  <div className={`absolute inset-0 bg-gradient-to-br ${c.tone}`} />
                    <CardContent className="relative p-6 h-full flex flex-col">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl grid place-items-center border border-white/10 ${c.iconCls}`}>
                        <c.Icon className="w-6 h-6 sm:w-7 sm:h-7" />
                      </div>
                      <Badge variant="outline" className={`w-fit uppercase tracking-widest text-[10px] font-black ${c.badgeCls}`}>
                        {c.badge}
                      </Badge>
                    </div>

                    <div className="mt-6">
                      <div className="text-xl font-black text-white tracking-tight">{c.title}</div>
                      <div className="mt-3 text-sm text-gray-400 leading-relaxed">{c.desc}</div>
                    </div>

                    <div className="mt-6 pt-2">
                      <Link href={c.href}>
                        <Button variant="link" className="p-0 h-auto font-black uppercase tracking-widest text-[11px] text-primary hover:text-primary/90">
                          {c.title === "AI News Digest" ? "Read Today's Digest" : c.title === "Global Coverage Map" ? "Explore Map" : "View Calendar"}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Main Content Area */}
      <section className="bg-[#0a101f] pt-10 pb-20 sm:pt-12">
        <div className="container mx-auto px-4 max-w-[2000px]">
          
            {/* Top Leaderboard Ad */}
            <motion.div {...revealProps(0.02)} className="relative">
              <AdSpace
                size="leaderboard"
                slotKey={leaderboardAd.slotKey}
                fallbackSlotKeys={leaderboardAd.fallbackSlotKeys}
                className="mb-10 rounded-2xl sm:mb-12"
                label={leaderboardAd.label}
              />
           </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-12">

            {/* Left Content Column */}
            <div className="lg:col-span-8 xl:col-span-9 2xl:col-span-8 space-y-20">
              
              {/* Broker Comparison Section */}
              <motion.div {...revealProps(0.03)}>
                <div className="mb-8 flex flex-col gap-4 sm:mb-10 md:flex-row md:items-end md:justify-between">
                  <div className="min-w-0">
                    <h2 className="whitespace-nowrap text-[clamp(1.45rem,6vw,1.95rem)] font-heading font-black uppercase tracking-tight text-white lg:text-3xl">
                      {heroCopy.brokersTitle || "Regulated Giants 2026"}
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">{heroCopy.brokersSubtitle || "Live spreads and verified regulation status from our independent lab."}</p>
                  </div>
                  <Button
                    variant="link"
                    className="h-11 w-full justify-between p-0 text-left text-sm font-bold text-primary transition-colors hover:text-white sm:h-auto sm:w-auto sm:justify-start sm:text-base"
                    onClick={() => window.location.href='/brokers'}
                  >
                    View Detailed Comparison <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                
                <div className="grid gap-6">
                  {featuredBrokers.length ? (
                    featuredBrokers.map((b, idx) => (
                      <motion.div key={b.id} {...revealProps(idx * 0.06)}>
                        <BrokerCard
                          id={b.slug}
                          name={b.name}
                          rating={Number.parseFloat(String(b.overallRating ?? 0)) || 0}
                          reviewsCount={b.reviewsCount ?? 0}
                          logoColor={["bg-green-700", "bg-black", "bg-yellow-500"][idx % 3]}
                          logoUrl={b.logo}
                          minDeposit={b.minDeposit || "—"}
                          maxLeverage={String(b.maxLeverage || (b as any).leverage || "—").replace(/^1:/, "") || "—"}
                          spread={b.spreadsFrom || b.spreads || "—"}
                          regulator={getRegulatorsLabel(b)}
                          accountType={Array.isArray((b as any).accountTypes) && (b as any).accountTypes[0]?.name ? String((b as any).accountTypes[0].name) : "Standard Account"}
                          isInspiredLayout={true}
                          isSpotlight={Number(b.displayOrder || 0) >= 1 && Number(b.displayOrder || 0) <= 3}
                          listBadgeText={null}
                          wellPerforming={false}
                          topRank={Number(b.displayOrder || 0) >= 1 && Number(b.displayOrder || 0) <= 10 ? Number(b.displayOrder || 0) : undefined}
                          openAccountUrl={b.ctaUrl || b.website}
                          isCompareSelected={selectedForCompare.includes(b.slug)}
                          onCompareToggle={handleCompareToggle}
                          compareCount={selectedForCompare.length}
                        />
                      </motion.div>
                    ))
                  ) : (
                    <div className="text-gray-500">No brokers available.</div>
                  )}
                </div>
                {selectedForCompare.length > 0 ? createPortal(
                  <motion.div
                    initial={{ y: 100 }}
                    animate={{ y: 0, opacity: 1 }}
                    role="region"
                    aria-live="polite"
                    aria-label="Brokers selected for comparison"
                    className="fixed bottom-[88px] left-1/2 z-[70] w-full max-w-xl -translate-x-1/2 px-4 sm:bottom-8"
                  >
                    <div className="rounded-2xl border border-primary/20 bg-[#161e2e]/90 p-3 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl sm:flex sm:items-center sm:justify-between sm:gap-6 sm:p-4">
                      <div className="flex min-w-0 items-start justify-between gap-3 sm:items-center sm:gap-4">
                        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                        <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 sm:flex">
                          <Zap className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[11px] font-black uppercase tracking-wider text-white sm:text-xs sm:tracking-widest">
                            {selectedForCompare.length} {selectedForCompare.length === 1 ? "Broker" : "Brokers"} Selected
                          </div>
                          <div className="mt-0.5 truncate text-[9px] font-bold uppercase tracking-wider text-gray-500 sm:tracking-widest">
                            {selectedForCompare.length < 3 ? `Add ${3 - selectedForCompare.length} more to compare` : "Ready to compare side-by-side"}
                          </div>
                        </div>
                        </div>
                        <Button
                          variant="ghost"
                          onClick={() => setSelectedForCompare([])}
                          className="h-8 shrink-0 px-2 text-[10px] font-black uppercase tracking-wider text-gray-400 hover:text-white sm:hidden"
                        >
                          Clear
                        </Button>
                      </div>
                      <div className="mt-3 sm:mt-0 sm:flex sm:shrink-0 sm:items-center sm:gap-2">
                        <Button
                          variant="ghost"
                          onClick={() => setSelectedForCompare([])}
                          className="hidden h-10 px-2 text-[10px] font-black uppercase tracking-wider text-gray-400 hover:text-white sm:inline-flex sm:px-3 sm:tracking-widest"
                        >
                          Clear
                        </Button>
                        <Link href={`/broker-comparison?ids=${selectedForCompare.join(",")}`} className="block min-w-0">
                          <Button className="h-10 w-full rounded-xl bg-primary px-4 text-[10px] font-black uppercase tracking-wider text-black transition-transform hover:scale-105 sm:h-11 sm:w-auto sm:px-8 sm:tracking-widest">
                            Compare Now
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                , document.body) : null}
              </motion.div>

              {/* HIGH VISIBILITY SPONSOR BANNER - Compact */}
              {promoCfg.hotOffers.length ? (
                <motion.div {...revealProps(0.04)} className="space-y-6">
                  {promoCfg.hotOffers.map((hot: any, i: number) => (
                    <div key={hot.id || i} className="relative group">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-primary via-yellow-400 to-primary rounded-2xl blur opacity-30 group-hover:opacity-50 transition-opacity"></div>
                      <div className="relative bg-gradient-to-r from-[#1a1f35] via-[#252d4a] to-[#1a1f35] border border-primary/30 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row items-center justify-between gap-4 shadow-xl overflow-hidden">
                        <div className="absolute inset-0 bg-[url('/img/carbon-fibre.png')] opacity-10 pointer-events-none"></div>
                        <div className="absolute -right-16 -top-16 w-40 h-40 bg-primary/20 rounded-full blur-2xl pointer-events-none"></div>
                        <div className="space-y-2 max-w-lg relative z-10">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-red-500 text-white font-black text-[10px] px-2 py-0.5">
                              {hot.offerTagText}
                            </Badge>
                            <Badge className="bg-primary/20 text-primary font-bold text-[10px] border border-primary/30 px-2 py-0.5">
                              {hot.sponsoredTagText}
                            </Badge>
                          </div>
                          <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight">{hot.title}</h3>
                          <p className="text-gray-300 text-sm">{hot.subtitle}</p>
                          <div className="flex flex-wrap items-center gap-4 text-xs">
                            {(Array.isArray(hot.features) ? hot.features : []).slice(0, 4).map((f: string, idx: number) => (
                              <span key={`${f}-${idx}`} className="text-green-400 font-bold flex items-center gap-1">
                                <Check className="w-3 h-3" /> {f}
                              </span>
                            ))}
                          </div>
                        </div>
                        <Button
                          className="bg-gradient-to-r from-primary to-yellow-400 text-black hover:from-yellow-400 hover:to-primary font-black px-6 py-3 text-sm rounded-xl shadow-lg shadow-primary/20 shrink-0 relative z-10 transition-all hover:scale-105 cursor-pointer"
                          onClick={() => (window.location.href = hot.buttonHref)}
                        >
                          {hot.buttonText}
                        </Button>
                      </div>
                    </div>
                  ))}
                </motion.div>
              ) : null}

              {/* Crypto Exchanges Section */}
              <motion.div {...revealProps(0.05)}>
                <div className="mb-8 flex flex-col gap-4 sm:mb-10 md:flex-row md:items-end md:justify-between">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-yellow-500 shadow-lg shadow-orange-500/20 sm:h-14 sm:w-14">
                      <Bitcoin className="h-6 w-6 text-white sm:h-7 sm:w-7" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="max-w-full text-[clamp(1.4rem,5.8vw,1.95rem)] font-heading font-black uppercase leading-[0.96] tracking-tight text-white break-words sm:text-[clamp(1.45rem,6vw,1.95rem)] lg:text-3xl">
                        {heroCopy.cryptoTitle || "Crypto Exchanges"}
                      </h2>
                      <p className="mt-1 max-w-2xl text-sm text-gray-500 sm:text-base">{heroCopy.cryptoSubtitle || "Compare fees, security, and trading features."}</p>
                    </div>
                  </div>
                  <Button
                    variant="link"
                    className="h-11 w-full justify-between p-0 text-left text-sm font-bold text-primary transition-colors hover:text-white sm:h-auto sm:w-auto sm:justify-start sm:text-base"
                    onClick={() => window.location.href='/crypto'}
                  >
                    View All Exchanges <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                
                <div className="w-full mx-auto grid sm:grid-cols-2 xl:grid-cols-3 gap-[6px]">
                  {featuredCrypto.map((exchange, idx) => (
                    <motion.div key={exchange.slug} {...revealProps(idx * 0.05)}>
                      <CryptoExchangeCard
                        href={`/crypto/${exchange.slug}`}
                        name={exchange.name}
                        tagline={(exchange as any).tagline || null}
                        logoUrl={(exchange as any).logo || null}
                        rating={(() => {
                          const r = Number.parseFloat(String((exchange as any).overallRating ?? 0));
                          return Number.isFinite(r) ? r : 0;
                        })()}
                        badge={String((exchange as any).exchangeType || "").trim() ? String((exchange as any).exchangeType).toUpperCase() : null}
                        featured={!!(exchange as any).featured}
                        metrics={[
                          { label: "Fee", value: String((exchange as any).spotTakerFee || (exchange as any).spotMakerFee || "—"), tone: "green" },
                          { label: "Coins", value: (exchange as any).totalCoinsListed ? `${(exchange as any).totalCoinsListed}+` : "—", tone: "neutral" },
                        ]}
                        ctaLabel="View Exchange"
                      />
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Tools & Services Section - Modern Grid */}
              <motion.div {...revealProps(0.06)} className="space-y-10">
                  <div className="text-left">
                   <h2 className="max-w-full text-[clamp(1.4rem,5.8vw,1.95rem)] font-heading font-black uppercase leading-[0.96] tracking-tight text-white break-words sm:text-[clamp(1.45rem,6vw,1.95rem)] lg:text-3xl">
                     {ecosystemTitle}
                   </h2>
                   <p className="mt-2 text-sm text-gray-500 sm:text-base">{ecosystemSubtitle}</p>
                  </div>
                 {isEcosystemSlider ? (
                   <Carousel
                     className="w-full"
                     plugins={[
                       Autoplay({
                         delay: 3500,
                         stopOnInteraction: false,
                         stopOnMouseEnter: true,
                       }),
                     ]}
                     opts={{ loop: true, align: "start" }}
                   >
                     <CarouselContent className="-ml-3 md:-ml-4">
                       {ecosystemCards.map((card) => (
                         <CarouselItem
                           key={card.id}
                           className="pl-3 md:pl-4 basis-[92%] sm:basis-[74%] md:basis-[56%] lg:basis-[50%] xl:basis-[46%]"
                         >
                           {renderEcosystemCard(card)}
                         </CarouselItem>
                       ))}
                     </CarouselContent>
                   </Carousel>
                 ) : (
                   <div className="grid md:grid-cols-2 gap-8">
                     {ecosystemCards.map((card) => (
                       <div key={card.id}>{renderEcosystemCard(card)}</div>
                     ))}
                   </div>
                 )}
              </motion.div>

              {/* High visibility native-looking ad - Compact */}
              {promoCfg.sponsoredOffers.length ? (
                <motion.div {...revealProps(0.07)} className="space-y-6">
                  {promoCfg.sponsoredOffers.map((sp: any, i: number) => (
                    <div
                      key={sp.id || i}
                      className="bg-gradient-to-r from-primary/10 to-[#161e2e] border border-primary/20 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row items-center justify-between gap-4 shadow-xl"
                    >
                      <div className="space-y-1.5 max-w-md">
                        <Badge className="bg-primary text-black font-black text-[10px] px-2 py-0.5">{sp.tagText}</Badge>
                        <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight">{sp.title}</h3>
                        <p className="text-gray-400 text-sm">{sp.subtitle}</p>
                      </div>
                      <Button
                        className="bg-primary text-black hover:bg-white font-black px-6 py-3 text-sm rounded-xl shadow-lg shadow-primary/10 shrink-0 cursor-pointer"
                        onClick={() => (window.location.href = sp.buttonHref)}
                      >
                        {sp.buttonText}
                      </Button>
                    </div>
                  ))}
                </motion.div>
              ) : null}

              {/* Market Insights */}
              <motion.div {...revealProps(0.08)} className="space-y-10">
                {/*
                  The news block was the only major section on this page with no
                  "see everything" exit — Brokers, Crypto and Bonuses all have
                  one. `/news` is the hub the whole vertical hangs off, so the
                  section needs a crawlable link to it from the homepage, not
                  just the four article links below.
                */}
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <h2 className="text-3xl font-heading font-black text-white uppercase tracking-tight">{heroCopy.insightsTitle || "Market Insights"}</h2>
                  <Link href="/news">
                    <Button
                      variant="link"
                      className="h-11 justify-start p-0 text-sm font-bold text-primary transition-colors hover:text-white sm:text-base"
                    >
                      View All News <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                    {marketInsights.length ? (
                      marketInsights.map((a) => (
                         <motion.div key={a.id} {...revealProps(0.02)}>
                           <Link href={`/news/${a.slug || a.id}`}>
                              <Card className="group cursor-pointer h-full bg-[#0b1222] border border-white/10 hover:border-primary/30 hover:bg-[#10182b] rounded-3xl overflow-hidden shadow-[0_18px_60px_rgba(0,0,0,0.45)] transition-all duration-500">
                               <div className="relative aspect-[16/10] bg-[#070b14] overflow-hidden">
                                 <img
                                   src={a.image || newsPlaceholderAsset}
                                   alt={a.title}
                                   loading="lazy"
                                   decoding="async"
                                   className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-[1.04] group-hover:opacity-100"
                                 />
                                 <div className="absolute inset-0 bg-gradient-to-t from-[#0a101f] via-[#0a101f]/35 to-transparent" />
                                 <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(ellipse_at_top,rgba(234,179,8,0.18),transparent_55%)]" />

                                 <div className="absolute left-4 top-4 flex items-center gap-2">
                                   <span className="inline-flex items-center rounded-full border border-primary/25 bg-black/40 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-primary backdrop-blur-md">
                                     {a.category || "Insight"}
                                   </span>
                                 </div>
                                   {/* The date used to sit here as an overlay on the
                                       thumbnail. It now lives in the meta row under
                                       the image, with reading time and views, so it
                                       matches the article cards used everywhere else
                                       instead of competing with the photo. */}
                               </div>

                                <CardContent className="p-6 flex flex-col gap-3">
                                  {/*
                                    Meta row first, then title — the same order and
                                    styling as the article cards on /news, so a reader
                                    sees when it was filed before what it says. Every
                                    part is machine-readable too: `<time dateTime>`
                                    carries the ISO value while the visible text stays
                                    in the reader's own timezone.
                                  */}
                                  <div className="flex flex-wrap items-center gap-3 text-[10px] font-black uppercase tracking-widest text-gray-500">
                                    {newsIsoDateTime(a) ? (
                                      <time
                                        dateTime={newsIsoDateTime(a)}
                                        title={localTimeZone ? `Your local time (${localTimeZone})` : "Published"}
                                      >
                                        {newsDateTimeLabel(a)}
                                      </time>
                                    ) : null}
                                    <span className="opacity-40">•</span>
                                    <span className="inline-flex items-center gap-1">
                                      <Clock className="w-3 h-3" /> {newsReadingTimeMin(a as any)} min
                                    </span>
                                    <span className="opacity-40">•</span>
                                    <span className="inline-flex items-center gap-1">
                                      <Eye className="w-3 h-3" /> {newsViewsLabel(a as any)}
                                    </span>
                                  </div>

                                  <h3
                                   className="font-heading font-black text-[20px] leading-[1.15] tracking-tight text-white group-hover:text-primary transition-colors"
                                   style={{
                                     display: "-webkit-box",
                                     WebkitLineClamp: 2,
                                     WebkitBoxOrient: "vertical",
                                     overflow: "hidden",
                                   }}
                                 >
                                   {a.title}
                                 </h3>

                                 {a.excerpt ? (
                                   <p
                                     className="text-sm text-gray-400 leading-relaxed"
                                     style={{
                                       display: "-webkit-box",
                                       WebkitLineClamp: 2,
                                       WebkitBoxOrient: "vertical",
                                       overflow: "hidden",
                                     }}
                                   >
                                     {a.excerpt}
                                   </p>
                                 ) : null}

                                 <div className="mt-auto pt-2 flex items-center justify-between">
                                   <span className="text-[10px] font-black uppercase tracking-[0.22em] text-gray-500 group-hover:text-gray-300 transition-colors">
                                     Read Insight
                                   </span>
                                   <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-primary transition-colors" />
                                 </div>
                               </CardContent>
                             </Card>
                           </Link>
                         </motion.div>
                       ))
                     ) : (
                       <div className="text-gray-500">No insights available.</div>
                     )}
                </div>
              </motion.div>

            </div>

            {/* Right Sidebar */}
            <aside className="lg:col-span-4 xl:col-span-3 2xl:col-span-4 space-y-10">
              
              {/* PREMIUM CRYPTO SPONSOR - High Visibility */}
              {promoCfg.exclusiveOffers.map((ex: any, i: number) => (
                <div
                  key={ex.id || i}
                  className="relative group cursor-pointer"
                  role="link"
                  tabIndex={0}
                  onClick={() => (window.location.href = ex.buttonHref)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      window.location.href = ex.buttonHref;
                    }
                  }}
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-500 rounded-3xl blur-sm opacity-60 group-hover:opacity-80 transition-opacity animate-pulse"></div>
                  <div className="relative bg-gradient-to-br from-[#1a1510] via-[#1f1a12] to-[#1a1510] border border-orange-500/30 rounded-3xl p-6 overflow-hidden">
                    <div className="absolute inset-0 bg-[url('/img/carbon-fibre.png')] opacity-10 pointer-events-none"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/20 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <Badge className="bg-orange-500 text-white font-black text-[10px] animate-pulse">{ex.badgeText}</Badge>
                        <span className="text-[10px] text-gray-500 font-bold">{ex.adLabel}</span>
                      </div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center shadow-lg overflow-hidden border border-orange-500/30">
                          {ex.logoUrl ? (
                            <img
                              src={ex.logoUrl}
                              alt={ex.brandName}
                              className="w-full h-full object-contain p-2 rounded-2xl bg-black/10"
                              loading="lazy"
                            />
                          ) : (
                            <Bitcoin className="w-8 h-8 text-white" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-black text-xl text-white">{ex.brandName}</h4>
                          <p className="text-orange-400 text-xs font-bold">{ex.brandSubtitle}</p>
                        </div>
                      </div>
                      <div className="bg-black/30 rounded-2xl p-4 mb-4 border border-orange-500/20">
                        <div className="text-3xl font-black text-white mb-1">{ex.offerTitle}</div>
                        <div className="text-orange-400 text-sm font-medium">{ex.offerSubtitle}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
                        {(Array.isArray(ex.features) ? ex.features : []).slice(0, 6).map((f: string, idx: number) => (
                          <div key={`${f}-${idx}`} className="flex items-center gap-1 text-gray-400">
                            <Check className="w-3 h-3 text-green-500" /> {f}
                          </div>
                        ))}
                      </div>
                      <Button
                        className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white hover:from-orange-600 hover:to-yellow-600 font-black rounded-xl h-14 shadow-lg shadow-orange-500/20 text-lg cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = ex.buttonHref;
                        }}
                      >
                        {ex.buttonText}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

                {/* Premium Sidebar Ad Spot */}
                <AdSpace
                  size="rectangle"
                  slotKey={rectangleAd.slotKey}
                  fallbackSlotKeys={rectangleAd.fallbackSlotKeys}
                  label={rectangleAd.label}
                  className="rounded-2xl"
                />

                <SocialLinksCard />

              {/* High Conversion Signup */}
              <Card className="bg-gradient-to-b from-[#161e2e] to-[#0a101f] border-primary/10 shadow-2xl relative overflow-hidden">
                <div className="absolute -right-20 -top-20 w-40 h-40 bg-primary opacity-5 rounded-full blur-3xl"></div>
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-primary text-black rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-primary/20">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <h3 className="font-black text-2xl mb-2 text-white uppercase tracking-tighter">Institutional Edge</h3>
                  <p className="text-gray-400 text-sm mb-8 leading-relaxed">Join 15,000+ traders receiving our daily algorithmic market sentiment reports.</p>
                  <div className="space-y-4">
                    <Input
                      value={edgeEmail}
                      onChange={(e) => setEdgeEmail(e.target.value)}
                      placeholder="Enter professional email"
                      className="bg-[#070b14] border-white/10 text-white h-14 rounded-xl px-6 focus:border-primary/50 transition-colors"
                    />
                    <Button
                      className="w-full bg-primary text-black hover:bg-white font-black h-14 rounded-xl shadow-lg shadow-primary/10"
                      disabled={edgeLoading}
                      onClick={async () => {
                        if (!edgeEmail.trim()) return;
                        setEdgeLoading(true);
                        try {
                          const res = await fetch("/api/newsletter/subscribe", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ email: edgeEmail.trim(), source: "institutional-edge" }),
                          });
                          const data = await res.json().catch(() => ({}));
                          if (res.ok) {
                            setEdgeDone(true);
                            setEdgeEmail("");
                            toast({ title: "Subscribed!", description: "You'll receive our market insights." });
                            window.setTimeout(() => setEdgeDone(false), 2500);
                          } else {
                            toast({
                              title: data?.message === "Already subscribed!" ? "Already subscribed" : "Error",
                              description: data?.message || "Failed to subscribe",
                              variant: data?.message === "Already subscribed!" ? "default" : "destructive",
                            });
                          }
                        } catch {
                          toast({ title: "Error", description: "Failed to subscribe. Please try again.", variant: "destructive" });
                        } finally {
                          setEdgeLoading(false);
                        }
                      }}
                    >
                      {edgeDone ? "Subscribed" : edgeLoading ? "Subscribing…" : "Access Free Insights"}
                    </Button>
                  </div>
                  <p className="text-[10px] text-gray-600 mt-6 text-center font-bold uppercase tracking-widest italic">No spam. Only high-alpha data.</p>
                </CardContent>
              </Card>

              {/* Bonus Scarcity Widget */}
              <Card className="relative bg-[#161e2e] border-white/5 overflow-hidden">
                <div
                  className="pointer-events-none absolute inset-0 opacity-80"
                  style={{
                    backgroundImage:
                      "radial-gradient(60% 60% at 18% 8%, rgba(234,179,8,0.14), transparent 58%), radial-gradient(70% 70% at 92% 42%, rgba(59,130,246,0.10), transparent 62%)",
                  }}
                />

                <div className="relative bg-[#070b14] p-5 border-b border-white/5 flex items-center justify-between">
                  <div>
                    <div className="font-black uppercase tracking-tighter text-gray-300 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-primary" /> Promotions
                    </div>
                    {/* gray-400 (#99a1af) on this card's #070b14 is 7.6:1;
                        gray-500 (#6a7282) was 4.06:1 and failed the audit. */}
                    <div className="text-[11px] text-gray-400 font-bold mt-1">Verified offers you can claim now</div>
                  </div>

                  {activeBonuses.length > 0 && activeBonuses.some((b) => {
                    if (!b.expiresAt) return false;
                    const ms = new Date(b.expiresAt).getTime();
                    return Number.isFinite(ms) && ms - Date.now() < 3 * 24 * 60 * 60 * 1000;
                  }) ? (
                    <Badge className="bg-red-500 text-white text-[10px] animate-pulse">EXPIRING SOON</Badge>
                  ) : null}
                </div>

                <CardContent className="relative p-4">
                  {activeBonuses.length > 0 ? (
                    <div className="space-y-4">
                      {activeBonuses.map((bonus) => {
                        const b = getBrokerAvatar(bonus.brokerId);
                        const href = bonus.slug ? `/bonuses/${bonus.slug}` : "/bonuses";
                        return (
                          <Link key={bonus.id} href={href} className="block">
                            <div className="group relative rounded-2xl border border-white/10 bg-[#0a101f]/35 bg-gradient-to-b from-white/[0.05] to-transparent hover:bg-[#0a101f]/45 hover:from-white/[0.08] transition-colors cursor-pointer">
                              <div className="p-4 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-4 min-w-0">
                                  <div className="shrink-0">
                                    <SquareLogoBadge logoUrl={b.logo} initials={b.initials} alt={b.name} />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-primary/90">
                                      {bonus.bonusType || "Offer"}
                                    </div>
                                    {/* `line-clamp-2`, not `truncate`: offer titles such as
                                        "DEPOSIT BONUS 50% UP TO $500" were being cut at
                                        164 px of a 225 px line, hiding the actual amount. */}
                                    <div className="font-black text-white text-sm uppercase tracking-tight mt-1 line-clamp-2">
                                      {bonus.title}
                                    </div>
                                    <div className="text-[11px] text-gray-500 font-bold mt-1 line-clamp-1">{b.name}</div>
                                  </div>
                                </div>

                                <div className="shrink-0 text-right">
                                  <div className="text-sm font-black text-green-400 tracking-tight">
                                    {bonus.bonusAmount || ""}
                                  </div>
                                  <div className="mt-2 w-9 h-9 rounded-xl border border-white/10 bg-white/5 grid place-items-center group-hover:border-primary/30 group-hover:bg-white/10 transition-colors">
                                    <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-primary transition-colors" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-gray-500 text-sm">No active offers at this time</div>
                  )}

                  <div className="mt-4 pt-4 border-t border-white/5">
                    <Link href="/bonuses">
                      <Button
                        variant="outline"
                        className="w-full rounded-2xl border-white/10 bg-white/5 text-white hover:bg-white/10 font-black uppercase tracking-widest text-xs h-11"
                      >
                        View All {activeBonusesCount}+ Offers
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

               {/* Long Skyscraper Ad */}
              <AdSpace
                size="skyscraper"
                slotKey={skyscraperAd.slotKey}
                fallbackSlotKeys={skyscraperAd.fallbackSlotKeys}
                label={skyscraperAd.label}
                className="rounded-3xl"
              />

            </aside>
          </div>
        </div>
      </section>
    </Layout>
  );
}
